import { useCoverageBetaStore } from "@/store/useCoverageBetaStore";
import { getDetectionBoxGeometry } from "@/utils/detection-box-geometry";
import { useClickWithoutDrag } from "@/hooks/useClickWithoutDrag";
import { useMemo } from "react";
import { OrientedBoxOverlay } from "./OrientedBoxOverlay";

interface Props {
  productDetection: any;
  productId: string;
}

export const QueryProductDetectionBox = (props: Props) => {
  const { productDetection, productId } = props;
  const points = productDetection?.points ?? [];
  // The div is the hit area; the overlay strokes the detection's shape.
  const { style: boundingBoxStyle, corners } = useMemo(
    () => getDetectionBoxGeometry(points),
    [points],
  );
  const setActiveProductId = useCoverageBetaStore((s) => s.setActiveProductId);
  const requestPlanogramFocus = useCoverageBetaStore(
    (s) => s.requestPlanogramFocus,
  );
  const { onPointerDown, isClick } = useClickWithoutDrag();
  const isActive = useCoverageBetaStore((s) => s.activeProductId === productId);
  // Same teal highlight as the reference/planogram: this product is a neighbour
  // of the hovered empty area, so it lights up here on the query image too.
  const isNeighbourHighlighted = useCoverageBetaStore(
    (s) => !!productId && s.neighbourProductIds.includes(productId),
  );
  // The reverse direction (DTOPS-272): a product hovered on the planogram points
  // back at its box here. Same teal as above — both mean "the product you're
  // looking at is over here" — but with a fill, since this cue has to be found
  // on a shelf photo rather than on the planogram's clean plates.
  const isLocated = useCoverageBetaStore(
    (s) => !!productId && s.locatedProductId === productId,
  );

  // Precedence, most specific first. Assigned rather than nested ternaries, per
  // the repo convention.
  let quadClassName = "stroke-gray-100/50";
  if (isLocated) quadClassName = "stroke-teal-400 fill-teal-400/20";
  else if (isNeighbourHighlighted) quadClassName = "stroke-teal-400";

  let quadRingClassName: string | undefined;
  if (isLocated || isNeighbourHighlighted)
    quadRingClassName = "stroke-teal-400";
  else if (isActive) quadRingClassName = "stroke-yellow-200";

  return (
    <div
      className="absolute cursor-pointer"
      onMouseEnter={() => setActiveProductId(productId)}
      onMouseLeave={() => setActiveProductId("")}
      onPointerDown={onPointerDown}
      // Frame this product on the planogram, if that's the shown view.
      onClick={(e) => {
        if (isClick(e)) requestPlanogramFocus(productId);
      }}
      style={{ ...boundingBoxStyle }}
    >
      <OrientedBoxOverlay
        corners={corners}
        className={quadClassName}
        ringClassName={quadRingClassName}
      />
    </div>
  );
};
